<!-- Select / Transfer Appraiser dialog -->
<p-dialog
  [header]="dialogHeader()"
  [visible]="visible()"
  (visibleChange)="visible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [maximizable]="false"
  [style]="{ width: 'min(95vw, 1200px)', height: '80vh', 'max-height': '760px' }"
  styleClass="select-appraiser-dialog"
  [breakpoints]="{ '768px': { width: '100vw', height: '100vh', 'max-height': '100vh', 'border-radius': '0' } }"
  (onHide)="onHide()">

  <!-- Claim metadata bar — Desktop (md+) -->
  <div class="metadata-bar hidden md:flex items-center gap-2.5 bg-[#f8f8fa] border border-[#e0e0e0] rounded p-2 mb-4 text-sm">
    <div class="flex-1">
      <span>Claim: </span><strong>{{ claimNumber() }}</strong>
      <br />
      <span>Unit: </span><strong>{{ unitName() || '–' }}</strong>
    </div>
    <div class="flex-1">
      <span>Appraiser: </span><strong>{{ currentAppraiser() }}</strong>
      <br />
      <span>Location: </span><strong>{{ initialAddress() || 'UNK' }}</strong>
    </div>
  </div>

  <!-- Claim metadata bar — Mobile (<md) -->
  <div class="metadata-bar-mobile md:hidden mb-4 text-sm bg-[#f8f8fa] rounded overflow-hidden">
    <div class="flex h-9 items-center justify-between px-3">
      <span>Claim: <strong>{{ claimNumber() }}</strong></span>
      <a class="text-[#0078d6] no-underline cursor-pointer inline-flex items-center gap-1"
        (click)="mobileDetailsExpanded.set(!mobileDetailsExpanded())">
        View Details
        <i class="pi text-[10px]" [class.pi-chevron-up]="mobileDetailsExpanded()" [class.pi-chevron-down]="!mobileDetailsExpanded()"></i>
      </a>
    </div>
    @if (mobileDetailsExpanded()) {
      <div class="px-3 py-2 flex flex-col gap-1 text-xs border-t border-[#e0e0e0]">
        <div><span>Unit: </span><strong>{{ unitName() || '–' }}</strong></div>
        <div><span>Appraiser: </span><strong>{{ currentAppraiser() }}</strong></div>
        <div><span>Location: </span><strong>{{ initialAddress() || 'UNK' }}</strong></div>
      </div>
    }
  </div>

  <!-- Actions toolbar -->
  <div class="toolbar flex flex-wrap items-center gap-2 mb-4">
    <div class="toolbar-address relative shrink-0 w-full md:w-auto md:min-w-[280px] md:max-w-[420px] md:flex-1" [title]="searchAddress()">
      <p-floatLabel class="w-full">
        <p-autocomplete
          inputId="appraiserSearchAddress"
          styleClass="w-full address-autocomplete"
          inputStyleClass="text-sm w-full"
          [ngModel]="searchAddress()"
          (ngModelChange)="searchAddress.set($event)"
          [suggestions]="addressSuggestions()"
          (completeMethod)="onAddressSearch($event)"
          (onSelect)="onAddressSelect($event)"
          [forceSelection]="false"
          [minLength]="3"
          [appendTo]="'body'" />
        <label for="appraiserSearchAddress">Search Address</label>
      </p-floatLabel>
      @if (addressModified()) {
        <button
          type="button"
          class="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded-full border-0 bg-transparent cursor-pointer text-[#6b7280] hover:text-[#374151] hover:bg-[#f3f4f6] transition-colors z-10"
          title="Reset to original address"
          (click)="onRefreshAddress()">
          <i class="pi pi-refresh text-sm"></i>
        </button>
      }
    </div>

    <!-- Bucket filters — Mobile: own row (full-width dropdown) -->
    <div class="md:hidden w-full">
      <p-select
        styleClass="w-full"
        [options]="mobileBucketOptions()"
        optionLabel="label"
        optionValue="value"
        optionDisabled="disabled"
        [ngModel]="activeFilter()"
        (ngModelChange)="selectFilter($event)"
        [appendTo]="'body'" />
    </div>

    <!-- Search All + filter — Mobile: own row -->
    @if (showSearchAll()) {
    <div class="md:hidden flex items-center gap-2 w-full">
      <div class="flex-1 min-w-0" [title]="searchAllSelectedValue() ?? ''">
        <ng-container *ngTemplateOutlet="searchAllAutocomplete" />
      </div>
      <button
        type="button"
        class="flex shrink-0 items-center justify-center w-9 h-9 rounded-full cursor-pointer border-0 bg-transparent text-[#0078d6] transition-colors"
        (click)="toggleColumnFilters()">
        <i class="pi text-lg" [class.pi-filter-fill]="showColumnFilters()" [class.pi-filter]="!showColumnFilters()"></i>
      </button>
    </div>
    }

    <!-- Bucket filters — Desktop (md+) -->
    <div class="toolbar-filters hidden md:flex gap-2 items-center ml-auto">
      @for (filter of appraiserFilters; track filter.value) {
        @let count = appraiserFilterCounts().get(filter.value) ?? 0;
        @let isActive = activeFilter() === filter.value && !searchAllActive();
        @let isLocked = !unlockedFilters().has(filter.value);
        @let isAddressBlocked = (filter.value === 'Staff' || filter.value === 'IA') && addressHasUnknowns();
        @let isDisabled = isLocked || count === 0 || isAddressBlocked;
        <span
          class="inline-flex"
          [pTooltip]="isAddressBlocked ? 'Full address required for ' + filter.value + ' appraisers' : (filter.noResultsLabel ?? filter.label)"
          tooltipPosition="top">
          <p-button
            [label]="filter.label + ' (' + count + ')'"
            size="small"
            [outlined]="!isActive"
            [disabled]="isDisabled"
            (onClick)="selectFilter(filter.value)"
            styleClass="whitespace-nowrap">
          </p-button>
        </span>
      }
      @if (showSearchAll()) {
      <div class="w-[220px]" [title]="searchAllSelectedValue() ?? ''">
        <ng-container *ngTemplateOutlet="searchAllAutocomplete" />
      </div>
      }
      <button
        type="button"
        class="flex items-center justify-center w-9 h-9 rounded-full cursor-pointer border-0 bg-transparent text-[#0078d6] transition-colors"
        (click)="toggleColumnFilters()">
        <i class="pi text-lg" [class.pi-filter-fill]="showColumnFilters()" [class.pi-filter]="!showColumnFilters()"></i>
      </button>
    </div>
  </div>

  <ng-template #searchAllAutocomplete>
    <p-autocomplete
      styleClass="w-full search-all-autocomplete"
      inputStyleClass="text-sm w-full"
      placeholder="Search All"
      [ngModel]="searchAllSelectedValue()"
      [suggestions]="appraiserNameSuggestions()"
      (completeMethod)="onAppraiserNameSearch($event)"
      (onSelect)="onAppraiserNameSelect($event)"
      (onClear)="onSearchAllClear()"
      (onBlur)="onSearchAllBlur($event)"
      [showClear]="searchAllActive()"
      [forceSelection]="false"
      [completeOnFocus]="true"
      [minLength]="0"
      [appendTo]="'body'" />
  </ng-template>

  <!-- Appraiser table -->
  @switch (loadState()) {
    @case ('loading') {
      <div class="flex items-center justify-center py-8">
        <i class="pi pi-spin pi-spinner text-2xl text-[#0078d6]"></i>
        <span class="ml-2 text-sm">Loading appraisers...</span>
      </div>
    }
    @case ('error') {
      <div class="flex items-center justify-center py-8">
        <span class="text-red-600 text-sm">{{ errorMessage() }}</span>
      </div>
    }
    @default {
  <div class="table-scroll-wrapper">
  <p-table
    [value]="filteredAppraisers()"
    [paginator]="filteredAppraisers().length > 20"
    [rows]="20"
    [rowsPerPageOptions]="[20, 50, 100]"
    sortMode="single"
    dataKey="id"
    styleClass="p-datatable-sm">
    <ng-template #header>
      <tr>
        <th style="width: 42px"></th>
        <th pSortableColumn="fullName">Full Name <p-sortIcon field="fullName" /></th>
        <th pSortableColumn="group">Group <p-sortIcon field="group" /></th>
        <th pSortableColumn="type">Type <p-sortIcon field="type" /></th>
        @if (showGrpColumns()) {
          <th pSortableColumn="certified" style="text-align: center">Certified <p-sortIcon field="certified" /></th>
          <th pSortableColumn="towing">Towing <p-sortIcon field="towing" /></th>
          <th pSortableColumn="distance">Distance <p-sortIcon field="distance" /></th>
        }
        <th pSortableColumn="availableDate">Available Date <p-sortIcon field="availableDate" /></th>
        <th pSortableColumn="assignedToday" class="text-center">Assigned Today <p-sortIcon field="assignedToday" /></th>
        <th pSortableColumn="dailyCount" class="text-center">Daily Count <p-sortIcon field="dailyCount" /></th>
        <th pSortableColumn="weeklyCount" class="text-center">Weekly Count <p-sortIcon field="weeklyCount" /></th>
      </tr>
      @if (showColumnFilters()) {
        <tr>
          <th></th>
          <th><input pInputText class="w-full text-sm p-1" placeholder="Value" (input)="updateColumnFilter('fullName', $any($event.target).value)" /></th>
          <th><input pInputText class="w-full text-sm p-1" placeholder="Value" (input)="updateColumnFilter('group', $any($event.target).value)" /></th>
          <th><input pInputText class="w-full text-sm p-1" placeholder="Value" (input)="updateColumnFilter('type', $any($event.target).value)" /></th>
          @if (showGrpColumns()) {
            <th><input pInputText class="w-full text-sm p-1" placeholder="Value" (input)="updateColumnFilter('certified', $any($event.target).value)" /></th>
            <th><input pInputText class="w-full text-sm p-1" placeholder="Value" (input)="updateColumnFilter('towing', $any($event.target).value)" /></th>
            <th><input pInputText class="w-full text-sm p-1" placeholder="Value" (input)="updateColumnFilter('distance', $any($event.target).value)" /></th>
          }
          <th><input pInputText class="w-full text-sm p-1" placeholder="Value" (input)="updateColumnFilter('availableDate', $any($event.target).value)" /></th>
          <th><input pInputText class="w-full text-sm p-1 text-center" placeholder="Value" (input)="updateColumnFilter('assignedToday', $any($event.target).value)" /></th>
          <th><input pInputText class="w-full text-sm p-1 text-center" placeholder="Value" (input)="updateColumnFilter('dailyCount', $any($event.target).value)" /></th>
          <th><input pInputText class="w-full text-sm p-1 text-center" placeholder="Value" (input)="updateColumnFilter('weeklyCount', $any($event.target).value)" /></th>
        </tr>
      }
    </ng-template>
    <ng-template #body let-appraiser>
      <tr (click)="selectedAppraiserId.set(appraiser.id)" class="cursor-pointer">
        <td>
          <p-radioButton [value]="appraiser.id" [ngModel]="selectedAppraiserId()" (ngModelChange)="selectedAppraiserId.set($event)" />
        </td>
        <td [attr.title]="appraiser.fullName">
          <a class="text-[#0078d6] no-underline cursor-pointer" (click)="showAppraiserInfo($event, appraiser)">{{ appraiser.fullName }}</a>
        </td>
        <td>{{ (appraiser.group | codeDescription:'usergroup' | async) || appraiser.group }}</td>
        <td>{{ (appraiser.type | codeDescription:'partyType' | async) || appraiser.type }}</td>
        @if (showGrpColumns()) {
          <td style="text-align: center">
              @if (appraiser.certified !== '–') {
                <a class="inline-flex items-center justify-center text-[#0078d6] cursor-pointer"
                   (click)="showCertifiedInfo($event, appraiser.certified)">
                  <i class="pi pi-check-circle text-base"></i>
                </a>
              } @else {
                <span class="text-[#6b7280]">–</span>
              }
            </td>
          <td [attr.title]="appraiser.towing">{{ appraiser.towing }}</td>
          <td [attr.title]="appraiser.distance">{{ appraiser.distance }}</td>
        }
        <td [attr.title]="appraiser.availableDate">{{ appraiser.availableDate }}</td>
        <td class="text-center" [attr.title]="appraiser.assignedToday">{{ appraiser.assignedToday }}</td>
        <td class="text-center" [attr.title]="appraiser.dailyCount">{{ appraiser.dailyCount }}</td>
        <td class="text-center" [attr.title]="appraiser.weeklyCount">{{ appraiser.weeklyCount }}</td>
      </tr>
    </ng-template>
    <ng-template #emptymessage>
      <tr>
        <td [attr.colspan]="showGrpColumns() ? 11 : 8" class="text-center text-sm text-[#666] py-6">{{ noAppraisersMessage() }}</td>
      </tr>
    </ng-template>
  </p-table>
  </div>
    }
  }

  <p-popover #certifiedPopover [appendTo]="'body'" (onShow)="onPopoverShow(certifiedPopover)">
    <div class="min-w-[220px] max-w-sm p-1">
      <h3 class="m-0 mb-2 text-sm font-semibold text-gray-800">
        Certifications
      </h3>
      <ul class="grid grid-cols-2 gap-x-8 gap-y-1.5 m-0 p-0 list-none text-sm text-gray-700 max-h-60 overflow-y-auto">
        @for (make of certifiedMakes(); track make) {
          <li>{{ make }}</li>
        }
      </ul>
    </div>
  </p-popover>

  <p-popover #appraiserPopover [appendTo]="'body'" (onShow)="onPopoverShow(appraiserPopover)">
    @if (quickInfoAppraiser(); as info) {
      <div class="flex flex-col gap-1 p-1 text-sm min-w-52" style="max-height: 40vh; overflow-y: auto;">

        <!-- Header row: name + copy button -->
        <div class="flex items-center justify-between gap-3">
          @if (info.name) { <span class="font-bold text-base leading-snug">{{ info.name }}</span> }
          <button
            type="button"
            class="shrink-0 ml-auto flex items-center justify-center w-8 h-8 rounded-lg border transition-colors cursor-pointer"
            [class]="copied() ? 'border-green-300 bg-green-100 text-green-600' : 'border-gray-200 bg-gray-100 text-gray-500 hover:bg-gray-200'"
            [pTooltip]="copied() ? 'Copied!' : 'Copy Name, Address &amp; Phone Number'"
            tooltipPosition="left"
            (click)="copyAppraiserInfo(info)">
            <i [class]="copied() ? 'pi pi-check text-sm' : 'pi pi-copy text-sm'"></i>
          </button>
        </div>

        <!-- Profile section -->
        @if (info.adjusterId || info.adjusterIdentifier || info.adjusterType || info.address || info.addressCounty !== null || info.coordinatorName) {
          <p class="m-0 mt-2 mb-0.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Profile</p>
          @if (info.adjusterId)             { <p class="m-0"><b>Adjuster ID:</b> {{ info.adjusterId }}</p> }
          @if (info.adjusterIdentifier)     { <p class="m-0"><b>Adjuster Identifier:</b> {{ info.adjusterIdentifier }}</p> }
          @if (info.adjusterType)           { <p class="m-0"><b>Adjuster Type:</b> {{ info.adjusterType }}</p> }
          @if (info.address)                { <p class="m-0"><b>Address:</b> {{ info.address }}</p> }
          @if (info.addressCounty !== null) { <p class="m-0"><b>Address County:</b> {{ info.addressCounty }}</p> }
          @if (info.coordinatorName)        { <p class="m-0"><b>Administrator:</b> {{ info.coordinatorName }}</p> }
        }

        <!-- Contact section -->
        @if (info.phoneNumber || info.cellPhone !== null || info.businessPhone !== null || info.businessExtension !== null || info.emailAddress || info.textAddress) {
          <p class="m-0 mt-2 mb-0.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Contact</p>
          @if (info.cellPhone !== null)         { <p class="m-0"><b>Cell Phone:</b> {{ info.cellPhone | phoneFormat }}</p> }
          @if (info.businessPhone !== null)     { <p class="m-0"><b>Business Phone:</b> {{ info.businessPhone | phoneFormat }}</p> }
          @if (info.businessExtension !== null) { <p class="m-0"><b>Business Extension:</b> {{ info.businessExtension }}</p> }
          @if (info.phoneNumber)                { <p class="m-0"><b>Phone:</b> {{ info.phoneNumber | phoneFormat }}</p> }
          @if (info.emailAddress)               { <p class="m-0"><b>Email:</b> <a [href]="'mailto:' + info.emailAddress" class="text-blue-500 hover:underline">{{ info.emailAddress }}</a></p> }
          @if (info.textAddress)                { <p class="m-0"><b>Text Address:</b> <a [href]="'mailto:' + info.textAddress" class="text-blue-500 hover:underline">{{ info.textAddress }}</a></p> }
        }

      </div>
    }
  </p-popover>

  <ng-template #footer>
    <p-button label="Cancel" [text]="true" (onClick)="onCancel()" />
    <p-button [label]="submitLabel()" [disabled]="!canContinue()" [loading]="submitting()" (onClick)="onContinue()" />
  </ng-template>
</p-dialog>

<!-- Decline Appraiser Modal — rendered only when the decline flow is enabled -->
@if (declineOptions()) {
  <p-dialog
    [header]="declineModalTitle()"
    [visible]="declineModalVisible()"
    (visibleChange)="declineModalVisible.set($event)"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: 'min(95vw, 520px)' }"
    styleClass="decline-appraiser-modal"
    (onHide)="onDeclineModalCancel()">

    <p class="text-sm leading-6 mb-4">
      To continue to the next best appraiser option, you acknowledge that the customer declined
      {{ declineFromFilter().toLowerCase() }} options. Please select a reason.
    </p>

    <div class="mb-4">
      <p-select
        class="w-full"
        styleClass="w-full"
        [options]="declineReasonOptions()"
        optionLabel="label"
        optionValue="value"
        [appendTo]="'body'"
        placeholder="Decline Reason"
        [ngModel]="declineReason()"
        (ngModelChange)="declineReason.set($event)" />
      @if (declineReasonSubmitted() && !declineReason()) {
        <p class="text-red-600 text-xs mt-1">Decline reason is required.</p>
      }
    </div>

    @if (declineReason() === 'OTHER') {
      <div>
        <textarea
          pTextarea
          rows="4"
          class="w-full"
          placeholder="Type other reason for declining {{ declineFromFilter() }} here"
          [ngModel]="declineReasonOther()"
          (ngModelChange)="declineReasonOther.set($event)"></textarea>
        @if (declineReasonSubmitted() && !declineReasonOther().trim()) {
          <p class="text-red-600 text-xs mt-1">Other reason is required.</p>
        }
      </div>
    }

    <ng-template #footer>
      <p-button label="Cancel" [text]="true" (onClick)="onDeclineModalCancel()" />
      <p-button [label]="declineContinueLabel()" [disabled]="!canContinueDeclineModal()" (onClick)="onDeclineModalContinue()" />
    </ng-template>
  </p-dialog>
}
